import { addDays, format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useLicenses } from "@/features/licensing/api/fetch-licenses";
import { AddOrganisationFormData } from "@/features/organisations/types/add-organisation-schema";
import { cn } from "@/lib/utils";
import { PublicLicense, ReplayTypeEnum } from "@classview/shared-types";

import { StartTrialModal } from "./start-trial-modal";

export function LicenseElement() {
  const { id: organisationId } = useParams();

  const form = useFormContext<AddOrganisationFormData>();
  const {
    data: licenses,
    isLoading: isLoadingLicenses,
    isSuccess: isSuccessLicenses,
  } = useLicenses();

  const licenseId = form.watch("licenseId");

  const selectedLicense: PublicLicense | undefined = isSuccessLicenses
    ? licenses?.find((license) => license.id === licenseId)
    : undefined;

  return (
    <>
      <FormField
        control={form.control}
        name="licenseId"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Pricing Plan</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={isLoadingLicenses || form.formState.isSubmitting}
            >
              <FormControl>
                <SelectTrigger className="capitalize">
                  <SelectValue placeholder="Select a pricing plan..." />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {isSuccessLicenses &&
                  licenses.map((license) => (
                    <SelectItem
                      key={license.id}
                      value={license.id}
                      className="capitalize"
                    >
                      {license.name.toLowerCase()}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="licenseEndDate"
        render={({ field }) => (
          <FormItem className="flex flex-col">
            <FormLabel isRequired>License End Date</FormLabel>

            <Popover>
              <PopoverTrigger asChild>
                <FormControl>
                  <Button
                    variant={"outline"}
                    className={cn(
                      "pl-3 text-left font-normal",
                      !field.value && "text-muted-foreground",
                    )}
                    disabled={form.formState.isSubmitting}
                  >
                    {field.value ? (
                      format(field.value, "PPP")
                    ) : (
                      <span>Pick a date</span>
                    )}
                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                  </Button>
                </FormControl>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={field.value}
                  onSelect={field.onChange}
                  disabled={(date) => date < addDays(new Date(), -1)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>

            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="concurrency"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Concurrency</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!selectedLicense || form.formState.isSubmitting}
                type="text"
                placeholder="Enter concurrency"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="replay"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Replay Version</FormLabel>
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={
                !selectedLicense ||
                isLoadingLicenses ||
                form.formState.isSubmitting
              }
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select a replay version..." />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value={ReplayTypeEnum.LITE} className="capitalize">
                  Lite
                </SelectItem>
                <SelectItem
                  value={ReplayTypeEnum.REPLAY}
                  className="capitalize"
                >
                  Replay
                </SelectItem>
                <SelectItem
                  value={ReplayTypeEnum.ADVANCED}
                  className="capitalize"
                >
                  Advanced
                </SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />
      {organisationId && <StartTrialModal />}
      <FormField
        control={form.control}
        name="storageInGB"
        render={({ field }) => (
          <FormItem>
            <FormLabel isRequired>Storage (GB)</FormLabel>
            <FormControl>
              <Input
                {...field}
                disabled={!selectedLicense || form.formState.isSubmitting}
                type="text"
                placeholder="Enter storage (GB)"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div>
        <Label className="text-sm text-muted-foreground">
          Additional Features
        </Label>

        <div className="mt-2 space-y-4">
          <FormField
            control={form.control}
            name="isGivenEventAccess"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="ml-2 mt-0">
                    <FormLabel className="text-base">
                      Enable Event Access
                    </FormLabel>
                  </div>
                </div>
              </FormItem>
            )}
          />

          <div className="flex flex-row items-center">
            <Switch defaultChecked={false} disabled={true} />
            <div className="ml-2 mt-0">
              <Label className="cursor-not-allowed text-base">
                Enable Marketplace Access
              </Label>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
